import React, { useState, useEffect } from 'react';
import nameDaysData from './namedays.json'; // Külső fájl importálása
import { 
  Zap, Shield, Cpu, Network, Flame, Mail, Phone, MapPin, 
  ChevronLeft, ChevronRight, Menu, X, ExternalLink, 
  Send, CheckCircle, Maximize2, Cookie, Calendar
} from 'lucide-react';

export default function App() {
  const [activeImage, setActiveImage] = useState(0);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const [currentDateString, setCurrentDateString] = useState("");
  const [currentNameDay, setCurrentNameDay] = useState("");
  const [cookieConsent, setCookieConsent] = useState(false);
  const [formStatus, setFormStatus] = useState({ submitted: false, error: false });

  const references = [
    { src: "001.JPG", title: "Ipari szekrények és vezérlés" },
    { src: "002.JPG", title: "Főelosztó és szünetmentes betáplálás" },
    { src: "003.jpg", title: "Szekrény belső huzalozás és automata elemek" },
    { src: "004.jpg", title: "Tűzvédelmi szellőztető és felügyeleti rendszer" },
    { src: "005.JPG", title: "Irodai kialakítás és strukturált hálózat" },
    { src: "007.JPG", title: "Elosztószekrény kivitelezés" },
    { src: "008.JPG", title: "Tetőszerkezeti tálcás kábelezés" },
    { src: "009.JPG", title: "Komplex kisfeszültségű elosztó" },
    { src: "010.JPG", title: "Csarnokvilágítás és szerelési munkálatok" }
  ];

  // Automatikus dátum és névnap lekérése a külső JSON-ból
  useEffect(() => {
    const today = new Date();
    
    // Dátum formázása
    const optionsDate = { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' };
    setCurrentDateString(today.toLocaleDateString('hu-HU', optionsDate));

    // Névnap lekérdezése a külső fájlból
    const month = today.getMonth() + 1;
    const day = today.getDate();
    const key = `${month}-${day}`;

    setCurrentNameDay(nameDaysData[key] || "Ünnepnap");
  }, []);

  // Automatikus képváltás galériához
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveImage((prev) => (prev + 1) % references.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [references.length]);

  const nextImage = () => setActiveImage((prev) => (prev + 1) % references.length);
  const prevImageLogic = () => setActiveImage((prev) => (prev - 1 + references.length) % references.length);

  const services = [
    { icon: <Zap className="w-6 h-6 text-yellow-400" />, title: "Épület villamosság" },
    { icon: <Cpu className="w-6 h-6 text-yellow-400" />, title: "Ipari Elektronika" },
    { icon: <Shield className="w-6 h-6 text-yellow-400" />, title: "Érintésvédelem" },
    { icon: <Zap className="w-6 h-6 text-yellow-400" />, title: "Villámvédelem" },
    { icon: <Network className="w-6 h-6 text-yellow-400" />, title: "Strukturált hálózat" },
    { icon: <Network className="w-6 h-6 text-yellow-400" />, title: "Gyengeáramú hálózat" },
    { icon: <Flame className="w-6 h-6 text-yellow-400" />, title: "Hő-és füst elvezető (RWA) rendszerek" }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    formData.append("access_key", "YOUR_WEB3FORMS_ACCESS_KEY_HERE"); 

    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body: formData
      });
      const data = await response.json();
      if (data.success) {
        setFormStatus({ submitted: true, error: false });
        e.target.reset();
      } else {
        setFormStatus({ submitted: false, error: true });
      }
    } catch (error) {
      setFormStatus({ submitted: false, error: true });
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 font-sans selection:bg-yellow-400 selection:text-zinc-950 pb-24 md:pb-0">
      
      {/* HEADER TOP BAR - DÁTUM ÉS NÉVNAP */}
      <div className="bg-yellow-400 text-zinc-950 px-4 py-2 text-xs font-bold tracking-wide">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            <span className="capitalize">{currentDateString || "Betöltés..."}</span>
          </div>
          <div className="bg-zinc-950 text-yellow-400 px-3 py-0.5 rounded-full text-xs font-black shadow-sm">
            Ma {currentNameDay || "..."} névapja van.
          </div>
        </div>
      </div>

      {/* HEADER */}
      <header className="sticky top-0 z-40 bg-zinc-900/90 backdrop-blur border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <img src="logo_new.png" alt="Radi és Társa Kft. Logó" className="w-12 h-12 rounded-xl object-cover shadow-md shadow-yellow-500/10" />
            <span className="text-xl sm:text-2xl font-black tracking-tight text-yellow-400">
              Radi és Társa <span className="text-white font-light">Kft.</span>
            </span>
          </div>

          <nav className="hidden md:flex items-center space-x-8 text-sm font-medium">
            <a href="#tevekenysegek" className="hover:text-yellow-400 transition-colors">Tevékenységek</a>
            <a href="#referenciak" className="hover:text-yellow-400 transition-colors">Referenciák</a>
            <a href="#kapcsolat" className="hover:text-yellow-400 transition-colors">Üzenetküldés</a>
            <a href="#elerhetosegek" className="hover:text-yellow-400 transition-colors">Elérhetőség</a>
          </nav>

          <div className="hidden md:block">
            <a href="tel:+36203131588" className="bg-yellow-400 hover:bg-yellow-500 text-zinc-950 px-5 py-2.5 rounded-xl font-bold transition-all shadow-lg shadow-yellow-400/20 flex items-center gap-2">
              <Phone className="w-4 h-4" /> +36 20 313 1588
            </a>
          </div>

          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden text-zinc-300 hover:text-white p-2">
            {mobileMenuOpen ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden bg-zinc-900 border-b border-zinc-800 px-4 pt-2 pb-6 space-y-4">
            <a href="#tevekenysegek" onClick={() => setMobileMenuOpen(false)} className="block text-lg font-medium hover:text-yellow-400">Tevékenységek</a>
            <a href="#referenciak" onClick={() => setMobileMenuOpen(false)} className="block text-lg font-medium hover:text-yellow-400">Referenciák</a>
            <a href="#kapcsolat" onClick={() => setMobileMenuOpen(false)} className="block text-lg font-medium hover:text-yellow-400">Üzenetküldés</a>
            <a href="#elerhetosegek" onClick={() => setMobileMenuOpen(false)} className="block text-lg font-medium hover:text-yellow-400">Elérhetőség</a>
            <a href="tel:+36203131588" className="w-full bg-yellow-400 text-zinc-950 py-3 rounded-xl font-bold flex items-center justify-center gap-2">
              <Phone className="w-5 h-5" /> Hívás most
            </a>
          </div>
        )}
      </header>

      {/* HERO SECTION */}
      <section className="relative bg-gradient-to-b from-zinc-900 to-zinc-950 py-16 lg:py-24 border-b border-zinc-800/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-yellow-400/10 border border-yellow-400/20 text-yellow-400 text-sm font-semibold mb-6">
            <Zap className="w-4 h-4" /> Megbízható villamosipari kivitelezés Miskolcon és környékén
          </div>
          <h1 className="text-4xl sm:text-6xl font-black tracking-tight text-white mb-6">
            Professzionális <span className="text-yellow-400">villamos rendszerek</span> építése
          </h1>
          <p className="text-zinc-400 text-lg sm:text-xl leading-relaxed mb-8">
            Komplett épületvillamosság, ipari elektronika, érintés- és villámvédelem, valamint gyengeáramú hálózatok tervezése és kivitelezése.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href="#kapcsolat" className="bg-yellow-400 hover:bg-yellow-500 text-zinc-950 px-8 py-4 rounded-xl font-bold text-lg transition-all shadow-xl shadow-yellow-400/10">
              Írjon nekünk üzenetet
            </a>
            <a href="#referenciak" className="bg-zinc-800 hover:bg-zinc-700 text-white px-8 py-4 rounded-xl font-bold text-lg transition-all border border-zinc-700">
              Referenciák megtekintése
            </a>
          </div>
        </div>
      </section>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 space-y-20">
        
        {/* TEVÉKENYSÉGEK ÉS GALÉRIA */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          
          <div id="tevekenysegek" className="lg:col-span-6 space-y-6">
            <div className="border-l-4 border-yellow-400 pl-4">
              <h2 className="text-3xl font-bold tracking-tight text-white">Főbb tevékenységi körünk</h2>
              <p className="text-zinc-400 text-sm mt-1">Teljes körű villamosipari megoldások partnereinknek.</p>
            </div>
            
            <div className="grid grid-cols-1 gap-3">
              {services.map((item, index) => (
                <div key={index} className="bg-zinc-900/80 hover:bg-zinc-800/80 border border-zinc-800 p-4 rounded-xl transition-all flex items-center space-x-4 group">
                  <div className="p-2.5 rounded-lg bg-yellow-400/10 group-hover:bg-yellow-400/20 transition-colors">
                    {item.icon}
                  </div>
                  <span className="text-lg font-semibold text-zinc-200 group-hover:text-yellow-400 transition-colors">
                    {item.title}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* GALÉRIA */}
          <div id="referenciak" className="lg:col-span-6 space-y-6">
            <div className="border-l-4 border-yellow-400 pl-4 flex justify-between items-end">
              <div>
                <h2 className="text-3xl font-bold tracking-tight text-white">Referenciáink</h2>
                <p className="text-zinc-400 text-sm mt-1">Automatikus diavetítés (kattintásra nagyítható).</p>
              </div>
            </div>

            <div className="bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden shadow-2xl group">
              <div 
                className="relative h-80 sm:h-96 bg-zinc-950 cursor-pointer overflow-hidden flex items-center justify-center"
                onClick={() => setLightboxImage(references[activeImage].src)}
              >
                <img 
                  src={references[activeImage].src} 
                  alt={references[activeImage].title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-all duration-700"
                />
                
                <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <span className="bg-yellow-400 text-zinc-950 px-4 py-2 rounded-xl font-bold flex items-center gap-2 shadow-lg">
                    <Maximize2 className="w-5 h-5" /> Nagyítás
                  </span>
                </div>

                <button 
                  onClick={(e) => { e.stopPropagation(); prevImageLogic(); }}
                  className="absolute left-3 bg-zinc-900/80 hover:bg-yellow-400 hover:text-zinc-950 text-white p-2.5 rounded-full backdrop-blur transition-all border border-zinc-700/50 z-10"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <button 
                  onClick={(e) => { e.stopPropagation(); nextImage(); }}
                  className="absolute right-3 bg-zinc-900/80 hover:bg-yellow-400 hover:text-zinc-950 text-white p-2.5 rounded-full backdrop-blur transition-all border border-zinc-700/50 z-10"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>

                <div className="absolute bottom-3 left-0 right-0 text-center z-10">
                  <span className="bg-zinc-950/80 text-yellow-400 px-4 py-1.5 rounded-full text-xs font-semibold backdrop-blur border border-zinc-800">
                    {references[activeImage].title}
                  </span>
                </div>
              </div>

              <div className="p-4 bg-zinc-900/90 border-t border-zinc-800 flex justify-center items-center gap-2 overflow-x-auto">
                {references.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveImage(idx)}
                    className={`h-2.5 rounded-full transition-all ${activeImage === idx ? 'w-8 bg-yellow-400' : 'w-2.5 bg-zinc-700 hover:bg-zinc-500'}`}
                  />
                ))}
              </div>
            </div>
          </div>

        </div>

        {/* ÜZENETKÜLDŐ ŰRLAP */}
        <div id="kapcsolat" className="bg-zinc-900 border border-zinc-800 rounded-3xl p-8 sm:p-12 shadow-2xl max-w-4xl mx-auto">
          <div className="text-center max-w-xl mx-auto mb-10">
            <h2 className="text-3xl font-bold text-white mb-2">Írjon nekünk üzenetet</h2>
            <p className="text-zinc-400 text-sm">Kérdése van, vagy árajánlatot szeretne kérni? Töltse ki az alábbi űrlapot!</p>
          </div>

          {formStatus.submitted ? (
            <div className="bg-green-500/10 border border-green-500/20 rounded-2xl p-8 text-center space-y-3">
              <CheckCircle className="w-12 h-12 text-green-400 mx-auto" />
              <h3 className="text-xl font-bold text-white">Üzenetét sikeresen elküldtük!</h3>
              <p className="text-zinc-300 text-sm">Köszönjük megkeresését. Munkatársunk hamarosan felveszi Önnel a kapcsolatot.</p>
              <button onClick={() => setFormStatus({ submitted: false, error: false })} className="mt-4 bg-zinc-800 text-white px-6 py-2 rounded-xl text-sm font-semibold hover:bg-zinc-700">
                Új üzenet küldése
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-2">Az Ön Neve</label>
                  <input type="text" name="name" required placeholder="Pl. Kovács János" className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-yellow-400 transition-colors" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-2">E-Mail Címe</label>
                  <input type="email" name="email" required placeholder="pelda@email.hu" className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-yellow-400 transition-colors" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">Üzenet / Igény leírása</label>
                <textarea name="message" rows="4" required placeholder="Írja le részletesen, miben tudunk segíteni..." className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-yellow-400 transition-colors"></textarea>
              </div>
              {formStatus.error && (
                <p className="text-red-400 text-sm text-center">Hiba történt az üzenet küldése során. Kérjük próbálja újra később, vagy hívjon minket telefonon.</p>
              )}
              <div className="text-center">
                <button type="submit" className="bg-yellow-400 hover:bg-yellow-500 text-zinc-950 font-bold px-8 py-4 rounded-xl transition-all shadow-lg shadow-yellow-400/10 inline-flex items-center gap-2">
                  <Send className="w-5 h-5" /> Üzenet elküldése
                </button>
              </div>
            </form>
          )}
        </div>

        {/* ELÉRHETŐSÉGEK */}
        <div id="elerhetosegek" className="bg-gradient-to-br from-yellow-400 to-amber-500 text-zinc-950 rounded-3xl p-8 sm:p-12 shadow-2xl relative overflow-hidden">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-3">Elérhetőségeink</h2>
            <p className="text-zinc-900 font-medium">Lépjen velünk kapcsolatba közvetlenül is!</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative z-10">
            <a href="mailto:radiestarsa@gmail.com" className="bg-zinc-950/10 hover:bg-zinc-950/20 backdrop-blur p-6 rounded-2xl border border-zinc-950/10 transition-all flex flex-col items-center text-center group">
              <div className="p-4 rounded-2xl bg-zinc-950 text-yellow-400 mb-4 shadow-lg group-hover:scale-110 transition-transform">
                <Mail className="w-7 h-7" />
              </div>
              <h3 className="font-bold text-lg mb-1">E-Mail</h3>
              <span className="text-zinc-900 font-semibold underline">radiestarsa@gmail.com</span>
            </a>

            <a href="tel:+36203131588" className="bg-zinc-950/10 hover:bg-zinc-950/20 backdrop-blur p-6 rounded-2xl border border-zinc-950/10 transition-all flex flex-col items-center text-center group">
              <div className="p-4 rounded-2xl bg-zinc-950 text-yellow-400 mb-4 shadow-lg group-hover:scale-110 transition-transform">
                <Phone className="w-7 h-7" />
              </div>
              <h3 className="font-bold text-lg mb-1">Telefon</h3>
              <span className="text-zinc-900 font-bold text-xl">+36 20 313 1588</span>
            </a>

            <div className="bg-zinc-950/10 backdrop-blur p-6 rounded-2xl border border-zinc-950/10 transition-all flex flex-col items-center text-center">
              <div className="p-4 rounded-2xl bg-zinc-950 text-yellow-400 mb-4 shadow-lg">
                <MapPin className="w-7 h-7" />
              </div>
              <h3 className="font-bold text-lg mb-1">Telephely</h3>
              <span className="text-zinc-900 font-semibold">Miskolc<br />Soltész-Nagy Kálmán u. 83.</span>
            </div>
          </div>
        </div>

      </main>

      {/* LIGHTBOX MODAL */}
      {lightboxImage && (
        <div className="fixed inset-0 z-50 bg-black/95 backdrop-blur-md flex items-center justify-center p-4" onClick={() => setLightboxImage(null)}>
          <button onClick={() => setLightboxImage(null)} className="absolute top-6 right-6 text-white bg-zinc-800 hover:bg-zinc-700 p-3 rounded-full transition-colors z-50">
            <X className="w-6 h-6" />
          </button>
          <div className="max-w-5xl max-h-[90vh] overflow-hidden rounded-2xl">
            <img src={lightboxImage} alt="Nagyított referencia" className="w-full h-auto max-h-[85vh] object-contain mx-auto" />
          </div>
        </div>
      )}

      {/* FELUGRÓ COOKIE / SÜTI SÁV */}
      {!cookieConsent && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-zinc-900/95 backdrop-blur border-t border-zinc-800 p-4 sm:p-6 shadow-2xl">
          <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-start space-x-3 text-left">
              <div className="p-2.5 rounded-xl bg-yellow-400/10 text-yellow-400 mt-0.5">
                <Cookie className="w-6 h-6" />
              </div>
              <p className="text-sm text-zinc-300">
                Weboldalunk sütiket (cookie-kat) használ a megfelelő működés és a felhasználói élmény biztosítása érdekében. Részletekért tekintse meg a <a href="suti.pdf" target="_blank" rel="noopener noreferrer" className="text-yellow-400 underline hover:text-yellow-300">Süti információ</a> PDF-et.
              </p>
            </div>
            <div className="flex items-center space-x-3 w-full sm:w-auto justify-end">
              <button 
                onClick={() => setCookieConsent(true)}
                className="bg-yellow-400 hover:bg-yellow-500 text-zinc-950 font-bold px-6 py-2.5 rounded-xl text-sm transition-all shadow-md w-full sm:w-auto"
              >
                Elfogadom
              </button>
            </div>
          </div>
        </div>
      )}

      {/* FOOTER */}
      <footer className="bg-zinc-900 border-t border-zinc-800 py-12 text-center text-zinc-400 text-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
          <div className="flex justify-center items-center space-x-3">
            <img src="logo_new.png" alt="Logó" className="w-8 h-8 rounded-lg object-cover" />
            <span className="font-bold text-white">Radi és Társa Kft.</span>
          </div>

          <div className="flex flex-wrap justify-center gap-6 text-sm font-medium">
            <a href="radi_es_tarsa_adatvedelmi.pdf" target="_blank" rel="noopener noreferrer" className="hover:text-yellow-400 transition-colors inline-flex items-center gap-1">
              Adatkezelési tájékoztató <ExternalLink className="w-3.5 h-3.5" />
            </a>
            <a href="radi_es_tarsa_panaszkezeles.pdf" target="_blank" rel="noopener noreferrer" className="hover:text-yellow-400 transition-colors inline-flex items-center gap-1">
              Panaszkezelési információ <ExternalLink className="w-3.5 h-3.5" />
            </a>
            <a href="radi_es_tarsa_suti_tajekoztato.pdf" target="_blank" rel="noopener noreferrer" className="hover:text-yellow-400 transition-colors inline-flex items-center gap-1">
              Süti információ <ExternalLink className="w-3.5 h-3.5" />
            </a>
            <a href="radi_es_tarsa_impresszum.pdf" target="_blank" rel="noopener noreferrer" className="hover:text-yellow-400 transition-colors inline-flex items-center gap-1">
              Impresszum <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>

          <div className="pt-6 border-t border-zinc-800/80 text-xs text-zinc-500">
            © {new Date().getFullYear()} Radi és Társa Kft. Minden jog fenntartva.
          </div>
        </div>
      </footer>

    </div>
  );
}